import type { Editor } from '@tiptap/react'
import { useEditorState } from '@tiptap/react'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useLanguageContext } from '../../../contexts'
import type { I18TranslationKey } from '../../../types'
import {
    applyTableTextStyle,
    canMergeSelectedTableCells,
    canSplitSelectedTableCell,
    getCurrentTableDomInfo,
    getCurrentTableSelectionBookmark,
    getSelectedTableCellCount,
    getTableSelectionBookmarkFromInfo,
    getTableSelectionInfo,
    getTableSelectionShape,
    getTableSelectionVariantFromShape,
    insertTableAtTarget,
    isTableTextStyleActive,
    prepareTableSelection,
    restoreTableSelectionBookmark,
    runTableAction,
    type TableActionType,
    type TableAxisTarget,
    type TableInsertDirection,
    type TableSelectionBookmark,
    type TableSelectionInfo,
    type TableSelectionTarget,
    type TableTextStyleAction
} from './core'
import {
    ALIGN_OPTIONS,
    AXIS_RENDER_CONFIGS,
    DEFAULT_ACTIVE_STYLE_MAP,
    type FloatingSurfaceState,
    type InsertIndicatorState,
    STYLE_ACTIONS,
    type TableToolbarDropdown} from './TableControls.config'
import {
    deriveSelectionState,
    getContextMenuPosition,
    openTableContextMenu,
    type OverlayState,
} from './TableControls.helpers'
import {
    ContextMenuSurface,
    FloatingToolbar,
    InsertIndicator,
    OverlayRoot,
    SelectionOutlineSegment
} from './TableControls.styles'
import { TableAxisControls } from './TableControlsAxis'
import {
    AlignPickerButton,
    ColorPickerButton,
    TableContextMenuContent,
    TableStructureActions,
    TableToolButton
} from './TableControlsMenu'

interface TableControlsProps {
    editor: Editor
    readonly?: boolean
    isMobile?: boolean
}

interface TableSelectionRetentionState {
    bookmark: TableSelectionBookmark
    tableStart: number
    expiresAt: number
}

interface TableSelectionPointerState {
    x: number
    y: number
}

export const TableControls: React.FC<TableControlsProps> = ({ editor, readonly, isMobile }) => {
    const { convertTextByLanguage } = useLanguageContext()
    const [overlayState, setOverlayState] = useState<OverlayState>(null)
    const [insertIndicator, setInsertIndicator] = useState<InsertIndicatorState>(null)
    const [activeSurface, setActiveSurface] = useState<FloatingSurfaceState>(null)
    const rootRef = useRef<HTMLDivElement | null>(null)
    const interactingRef = useRef(false)
    const draggingSelectionRef = useRef(false)
    const selectionPointerRef = useRef<TableSelectionPointerState | null>(null)
    const refreshRafRef = useRef<number | null>(null)
    const selectionRetentionRef = useRef<TableSelectionRetentionState | null>(null)

    const editorState = useEditorState({
        editor,
        selector: ({ editor: current }) => {
            if (!current || current.isDestroyed) {
                return {
                    focused: false,
                    editable: false,
                    selectionInfo: null,
                    canMerge: false,
                    canSplit: false,
                    activeStyleMap: DEFAULT_ACTIVE_STYLE_MAP,
                    activeAlign: null
                }
            }

            return {
                focused: current.isFocused,
                editable: current.isEditable,
                selectionInfo: getTableSelectionInfo(current),
                canMerge: canMergeSelectedTableCells(current),
                canSplit: canSplitSelectedTableCell(current),
                activeStyleMap: {
                    bold: isTableTextStyleActive(current, { type: 'bold' }),
                    italic: isTableTextStyleActive(current, { type: 'italic' }),
                    underline: isTableTextStyleActive(current, { type: 'underline' }),
                    strike: isTableTextStyleActive(current, { type: 'strike' })
                },
                activeAlign:
                    ALIGN_OPTIONS.find(item => isTableTextStyleActive(current, { type: 'textAlign', value: item.value }))?.value ?? null
            }
        }
    })

    const shouldRender =
        !readonly &&
        !isMobile &&
        editorState?.editable &&
        (editorState?.focused || interactingRef.current || draggingSelectionRef.current || Boolean(activeSurface))
    const selectionInfo = editorState?.selectionInfo ?? overlayState?.selection ?? null
    const canMerge = editorState?.canMerge ?? false
    const canSplit = editorState?.canSplit ?? false
    const activeStyleMap = editorState?.activeStyleMap ?? DEFAULT_ACTIVE_STYLE_MAP
    const activeAlign = editorState?.activeAlign ?? null
    const selectionState = useMemo(
        () =>
            deriveSelectionState({
                editor,
                tableElement: overlayState?.tableElement,
                anchorRects: overlayState?.anchorRects,
                selectionInfo,
                canMerge,
                canSplit,
                includeSingleCellActions: false
            }),
        [editor, overlayState?.tableElement, overlayState?.anchorRects, selectionInfo, canMerge, canSplit]
    )
    const contextMenuSelectionState = useMemo(
        () =>
            deriveSelectionState({
                editor,
                tableElement: overlayState?.tableElement,
                anchorRects: overlayState?.anchorRects,
                selectionInfo,
                canMerge,
                canSplit,
                includeSingleCellActions: true
            }),
        [editor, overlayState?.tableElement, overlayState?.anchorRects, selectionInfo, canMerge, canSplit]
    )
    const toolbarOpen =
        Boolean(selectionState?.capabilities) &&
        !draggingSelectionRef.current &&
        Boolean(selectionState && getSelectedTableCellCount(selectionState.selectionInfo) > 1)
    const contextMenu = activeSurface?.kind === 'contextMenu' ? activeSurface : null
    const activeDropdown = activeSurface?.kind === 'dropdown' ? activeSurface.dropdown : null

    const closeFloatingUi = useCallback(
        (options?: { keepContextMenu?: boolean; keepInsertIndicator?: boolean; keepDropdowns?: boolean }) => {
            setActiveSurface(current => {
                if (!current) {
                    return null
                }

                if (current.kind === 'contextMenu' && options?.keepContextMenu) {
                    return current
                }

                if (current.kind === 'dropdown' && options?.keepDropdowns) {
                    return current
                }

                return null
            })
            if (!options?.keepInsertIndicator) {
                setInsertIndicator(null)
            }
        },
        []
    )

    const resetInteractionState = useCallback(() => {
        interactingRef.current = false
        draggingSelectionRef.current = false
        selectionPointerRef.current = null
    }, [])

    const clearSelectionRetention = useCallback(() => {
        selectionRetentionRef.current = null
    }, [])

    const rememberSelectionRetention = useCallback(
        (currentSelectionInfo?: TableSelectionInfo | null) => {
            const nextSelectionInfo = currentSelectionInfo ?? getTableSelectionInfo(editor)
            if (!nextSelectionInfo) {
                return null
            }

            const variant = getTableSelectionVariantFromShape(getTableSelectionShape(nextSelectionInfo))
            if (variant === 'cell') {
                return null
            }

            const bookmark = getCurrentTableSelectionBookmark(editor) ?? getTableSelectionBookmarkFromInfo(nextSelectionInfo)

            const retention = {
                bookmark,
                tableStart: nextSelectionInfo.tableStart,
                expiresAt: performance.now() + 320
            }

            selectionRetentionRef.current = retention
            return retention
        },
        [editor]
    )

    const restoreRetainedSelection = useCallback(() => {
        const retention = selectionRetentionRef.current
        if (!retention) {
            return false
        }

        if (performance.now() > retention.expiresAt) {
            clearSelectionRetention()
            return false
        }

        const currentSelectionInfo = getTableSelectionInfo(editor)
        if (!currentSelectionInfo || currentSelectionInfo.tableStart !== retention.tableStart) {
            clearSelectionRetention()
            return false
        }

        const currentVariant = getTableSelectionVariantFromShape(getTableSelectionShape(currentSelectionInfo))
        if (currentVariant !== 'cell') {
            return false
        }

        return restoreTableSelectionBookmark(editor, retention.bookmark)
    }, [clearSelectionRetention, editor])

    const refreshOverlay = useCallback(() => {
        if (!shouldRender) {
            clearSelectionRetention()
            setOverlayState(null)
            return
        }

        const info = getCurrentTableDomInfo(editor)
        setOverlayState(info)
    }, [clearSelectionRetention, editor, shouldRender])

    const scheduleRetainedSelectionRecovery = useCallback(() => {
        requestAnimationFrame(() => {
            const restored = restoreRetainedSelection()
            refreshOverlay()

            requestAnimationFrame(() => {
                if (!restored) {
                    restoreRetainedSelection()
                }
                refreshOverlay()
            })
        })
    }, [refreshOverlay, restoreRetainedSelection])

    const prepareSelectionTarget = useCallback(
        (target: TableSelectionTarget) => {
            const variant = prepareTableSelection(editor, target)
            if (variant && variant !== 'cell') {
                rememberSelectionRetention()
                scheduleRetainedSelectionRecovery()
            } else {
                clearSelectionRetention()
            }
            refreshOverlay()
            return variant
        },
        [clearSelectionRetention, editor, refreshOverlay, rememberSelectionRetention, scheduleRetainedSelectionRecovery]
    )

    const executeTableCommand = useCallback(
        (
            command: () => boolean,
            options?: {
                closeContextMenu?: boolean
                closeDropdowns?: boolean
            }
        ) => {
            rememberSelectionRetention()
            const done = command()

            if (options?.closeContextMenu) {
                closeFloatingUi({
                    keepDropdowns: !options.closeDropdowns,
                    keepInsertIndicator: true
                })
            } else if (options?.closeDropdowns) {
                closeFloatingUi({
                    keepContextMenu: true,
                    keepInsertIndicator: true
                })
            }

            rememberSelectionRetention()
            refreshOverlay()
            return done
        },
        [closeFloatingUi, refreshOverlay, rememberSelectionRetention]
    )

    const executeTableAction = useCallback(
        (action: TableActionType, options?: { closeContextMenu?: boolean; closeDropdowns?: boolean }) =>
            executeTableCommand(() => runTableAction(editor, action), options),
        [editor, executeTableCommand]
    )

    const executeTableTextStyle = useCallback(
        (action: TableTextStyleAction, options?: { closeContextMenu?: boolean; closeDropdowns?: boolean }) =>
            executeTableCommand(() => applyTableTextStyle(editor, action), options),
        [editor, executeTableCommand]
    )

    const executeInsertAt = useCallback(
        (target: TableAxisTarget, direction: TableInsertDirection, options?: { closeContextMenu?: boolean }) =>
            executeTableCommand(() => insertTableAtTarget(editor, target, direction), options),
        [editor, executeTableCommand]
    )

    const interactWithTarget = useCallback(
        (
            target: TableSelectionTarget,
            options?: {
                contextMenuAt?: { x: number; y: number }
            }
        ) => {
            interactingRef.current = true

            if (options?.contextMenuAt) {
                prepareSelectionTarget(target)
                openTableContextMenu(setActiveSurface, options.contextMenuAt)
                return
            }

            prepareSelectionTarget(target)
        },
        [prepareSelectionTarget]
    )

    const openDropdownSurface = useCallback((dropdown: TableToolbarDropdown) => {
        setActiveSurface({ kind: 'dropdown', dropdown })
    }, [])

    const toggleDropdownSurface = useCallback((dropdown: TableToolbarDropdown) => {
        setActiveSurface(current => (current?.kind === 'dropdown' && current.dropdown === dropdown ? null : { kind: 'dropdown', dropdown }))
    }, [])

    const scheduleOverlayRefresh = useCallback(() => {
        if (refreshRafRef.current !== null) {
            return
        }

        refreshRafRef.current = window.requestAnimationFrame(() => {
            refreshRafRef.current = null
            refreshOverlay()
        })
    }, [refreshOverlay])

    useLayoutEffect(() => {
        refreshOverlay()
    }, [refreshOverlay])

    useEffect(() => {
        return () => {
            if (refreshRafRef.current !== null) {
                window.cancelAnimationFrame(refreshRafRef.current)
            }
        }
    }, [])

    useEffect(() => {
        if (!shouldRender) {
            return
        }

        const onSelectionChange = () => {
            const currentSelectionInfo = getTableSelectionInfo(editor)
            if (draggingSelectionRef.current && currentSelectionInfo) {
                rememberSelectionRetention(currentSelectionInfo)
            }
            scheduleOverlayRefresh()
        }
        const onResize = () => scheduleOverlayRefresh()
        const onScroll = () => scheduleOverlayRefresh()
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeFloatingUi()
            }
        }

        document.addEventListener('selectionchange', onSelectionChange)
        window.addEventListener('resize', onResize)
        window.addEventListener('scroll', onScroll, true)
        document.addEventListener('keydown', onKeyDown)

        return () => {
            document.removeEventListener('selectionchange', onSelectionChange)
            window.removeEventListener('resize', onResize)
            window.removeEventListener('scroll', onScroll, true)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [closeFloatingUi, editor, rememberSelectionRetention, scheduleOverlayRefresh, shouldRender])

    useEffect(() => {
        if (!shouldRender) {
            closeFloatingUi()
            resetInteractionState()
            clearSelectionRetention()
        }
    }, [clearSelectionRetention, closeFloatingUi, resetInteractionState, shouldRender])

    useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target
            if (!(target instanceof HTMLElement)) {
                resetInteractionState()
                return
            }
            interactingRef.current = Boolean(target.closest('[data-rich-text-table-control]'))
            const tableCell = target.closest('td.rich-text-table-cell, th.rich-text-table-header')
            draggingSelectionRef.current = false
            selectionPointerRef.current =
                !interactingRef.current && Boolean(tableCell)
                    ? {
                          x: event.clientX,
                          y: event.clientY
                      }
                    : null

            if (!interactingRef.current) {
                clearSelectionRetention()
                closeFloatingUi()
            }
        }

        const handlePointerUp = () => {
            requestAnimationFrame(() => {
                const shouldRestoreSelection = draggingSelectionRef.current
                const restored = shouldRestoreSelection ? restoreRetainedSelection() : false
                resetInteractionState()
                closeFloatingUi({
                    keepContextMenu: true,
                    keepDropdowns: true
                })
                refreshOverlay()

                requestAnimationFrame(() => {
                    if (shouldRestoreSelection && !restored) {
                        restoreRetainedSelection()
                    }
                    clearSelectionRetention()
                    refreshOverlay()
                })
            })
        }

        const handlePointerMove = (event: PointerEvent) => {
            if (!draggingSelectionRef.current) {
                const pointerState = selectionPointerRef.current
                if (!pointerState) {
                    return
                }

                const movedEnough =
                    Math.abs(event.clientX - pointerState.x) > 3 || Math.abs(event.clientY - pointerState.y) > 3

                if (!movedEnough) {
                    return
                }

                draggingSelectionRef.current = true
            }

            if (!draggingSelectionRef.current) {
                return
            }

            scheduleOverlayRefresh()
        }

        document.addEventListener('pointerdown', handlePointerDown, true)
        document.addEventListener('pointermove', handlePointerMove, true)
        document.addEventListener('pointerup', handlePointerUp, true)

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true)
            document.removeEventListener('pointermove', handlePointerMove, true)
            document.removeEventListener('pointerup', handlePointerUp, true)
        }
    }, [clearSelectionRetention, closeFloatingUi, refreshOverlay, resetInteractionState, restoreRetainedSelection, scheduleOverlayRefresh])

    useEffect(() => {
        if (!shouldRender) {
            return
        }

        const dom = editor.view.dom
        const handleContextMenu = (event: MouseEvent) => {
            const target = event.target
            if (!(target instanceof HTMLElement)) {
                return
            }

            const cell = target.closest('td.rich-text-table-cell, th.rich-text-table-header')
            if (!(cell instanceof HTMLElement) || !dom.contains(cell)) {
                return
            }

            event.preventDefault()
            interactWithTarget(
                { kind: 'cell', element: cell },
                {
                    contextMenuAt: {
                        x: event.clientX,
                        y: event.clientY
                    }
                }
            )
        }

        dom.addEventListener('contextmenu', handleContextMenu)
        return () => dom.removeEventListener('contextmenu', handleContextMenu)
    }, [interactWithTarget, shouldRender])

    if (!shouldRender || !overlayState) {
        return null
    }

    const { anchorRects } = overlayState
    const selectionOutlineSegments = selectionState?.outlineSegments ?? []
    const toolbarPosition = selectionState?.toolbarPosition ?? null
    const contextMenuPosition = getContextMenuPosition(contextMenu)

    return createPortal(
        <OverlayRoot ref={rootRef}>
            {selectionOutlineSegments.map((segment, index) => (
                <SelectionOutlineSegment
                    key={`${segment.top}:${segment.left}:${segment.width}:${segment.height}:${index}`}
                    style={{
                        top: segment.top,
                        left: segment.left,
                        width: segment.width,
                        height: segment.height
                    }}
                />
            ))}
            {AXIS_RENDER_CONFIGS.map(config => (
                <TableAxisControls
                    key={config.axis}
                    axisConfig={config}
                    anchorRects={anchorRects}
                    selectionState={selectionState}
                    convertTextByLanguage={convertTextByLanguage}
                    setInsertIndicator={setInsertIndicator}
                    interactWithTarget={interactWithTarget}
                    executeInsertAt={executeInsertAt}
                    interactingRef={interactingRef}
                />
            ))}

            {insertIndicator && (
                <InsertIndicator
                    axis={insertIndicator.axis}
                    style={{
                        top: insertIndicator.top,
                        left: insertIndicator.left,
                        width: insertIndicator.width,
                        height: insertIndicator.height
                    }}
                />
            )}

            {toolbarOpen && toolbarPosition && selectionState && (
                <div
                    data-rich-text-table-control=""
                    style={{
                        position: 'absolute',
                        top: toolbarPosition.top,
                        left: toolbarPosition.left,
                        transform: toolbarPosition.transform,
                        zIndex: 24
                    }}
                >
                    <FloatingToolbar>
                        <TableStructureActions actions={selectionState.capabilities.structureActions} onAction={executeTableAction} />
                        {STYLE_ACTIONS.map(action => (
                            <TableToolButton
                                key={action.type}
                                title={convertTextByLanguage(action.label as I18TranslationKey)}
                                icon={action.icon}
                                active={activeStyleMap[action.type]}
                                onClick={() => executeTableTextStyle({ type: action.type })}
                            />
                        ))}
                        <ColorPickerButton
                            opened={activeDropdown === 'textColor'}
                            onOpen={() => openDropdownSurface('textColor')}
                            onToggle={() => toggleDropdownSurface('textColor')}
                            icon="TextColor"
                            title={convertTextByLanguage('textColor')}
                            defaultValue={editor.storage.textColor}
                            onChange={value => {
                                editor.storage.textColor = value ?? ''
                                executeTableTextStyle({ type: 'textColor', value })
                            }}
                        />
                        <ColorPickerButton
                            opened={activeDropdown === 'bgColor'}
                            onOpen={() => openDropdownSurface('bgColor')}
                            onToggle={() => toggleDropdownSurface('bgColor')}
                            icon="TextBackGroundColor"
                            title={convertTextByLanguage('textBackgroundColor')}
                            defaultValue={editor.storage.bgColor}
                            onChange={value => {
                                editor.storage.bgColor = value ?? ''
                                executeTableTextStyle({ type: 'highlight', value })
                            }}
                        />
                        <AlignPickerButton
                            opened={activeDropdown === 'align'}
                            onToggle={() => toggleDropdownSurface('align')}
                            onClose={() => closeFloatingUi({ keepContextMenu: true, keepInsertIndicator: true })}
                            activeValue={activeAlign}
                            onSelect={value => executeTableTextStyle({ type: 'textAlign', value })}
                        />
                    </FloatingToolbar>
                </div>
            )}

            {contextMenu && contextMenuPosition && (
                <div
                    data-rich-text-table-control=""
                    style={{
                        position: 'absolute',
                        top: contextMenuPosition.top,
                        left: contextMenuPosition.left
                    }}
                >
                    <ContextMenuSurface>
                        <TableContextMenuContent
                            capabilities={contextMenuSelectionState?.capabilities ?? null}
                            onAction={action => executeTableAction(action, { closeContextMenu: true })}
                        />
                    </ContextMenuSurface>
                </div>
            )}
        </OverlayRoot>,
        document.body
    )
}
